import { doc, getDoc, setDoc, updateDoc, increment, Timestamp, collection } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { 
  GameRecord, 
  PlayerGameSummary, 
  PlayerStats, 
  PlayerStyleMetrics, 
  GameTurn,
  GameProcessingState,
  DeveloperGameAnalysis
} from '../types';
import { evaluateAchievements } from './achievementService';
import { updateGlobalRankings, checkAndAssignBadges } from './rankingService';
import { useNotificationStore } from '../store/useNotificationStore';

export const processFinishedGame = async (game: GameRecord): Promise<void> => {
  const currentUserId = auth.currentUser?.uid;
  if (!currentUserId) {
    console.warn('[Stats] No user logged in, skipping game processing.');
    return;
  }

  // 1. Validate idempotency
  const processingRef = doc(db, 'gameProcessing', game.id);
  const processingSnap = await getDoc(processingRef);
  
  // Note: We don't skip if processed, because we want to update the stats of the current user
  // even if the other player already processed the game.
  // However, we should check if THIS user has already processed it.
  // Actually, let's use a per-user processing flag if we want to be perfect,
  // but for now, we'll just check if the game is finished.

  // 2. Generate summaries
  const summary1 = generatePlayerSummary(game, game.player1Id);
  const summary2 = generatePlayerSummary(game, game.player2Id);

  // 3. Save summaries in game record (Only if not already done)
  if (!game.player1Summary || !game.player2Summary) {
    const gameRef = doc(db, 'games', game.id);
    try {
      await updateDoc(gameRef, {
        player1Summary: summary1,
        player2Summary: summary2,
        status: 'finished',
        finishedAt: Timestamp.now()
      });
    } catch (e) {
      // Might fail if already updated by other player, ignore
      console.log('[Stats] Game record already updated or permission denied.');
    }
  }

  // 4. Update stats ONLY for the current user
  const isPlayer1 = game.player1Id === currentUserId;
  const isPlayer2 = game.player2Id === currentUserId;
  const isHumanOpponent = game.opponentType === 'human';

  if (isPlayer1) {
    console.log(`[Stats] Updating stats for Player 1: ${currentUserId}`);
    await updatePlayerStats(game.player1Id, summary1, game, isHumanOpponent);
  } else if (isPlayer2) {
    console.log(`[Stats] Updating stats for Player 2: ${currentUserId}`);
    await updatePlayerStats(game.player2Id, summary2, game, isHumanOpponent);
  } else {
    console.warn(`[Stats] Current user ${currentUserId} is not a participant in game ${game.id}`);
  }

  // 5. Update head-to-head (Only if current user is participant)
  if (isPlayer1 || isPlayer2) {
    await updateHeadToHead(game.player1Id, game.player2Id, summary1, summary2, game);
  }

  // 6. Update developer metrics (Only if current user is participant)
  if (isPlayer1 || isPlayer2) {
    await updateDeveloperMetrics(game, summary1, summary2);
  }

  // 7. Evaluate achievements for current user
  if (isPlayer1 || isPlayer2) {
    await evaluateAchievements(currentUserId);
  }

  // 8. Mark as processed globally (optional, but good for reference)
  try {
    await setDoc(processingRef, {
      gameId: game.id,
      processed: true,
      processedAt: Timestamp.now()
    }, { merge: true });
  } catch (e) {
    // Ignore
  }

  // 9. Update global rankings and check for badges
  if (isPlayer1 || isPlayer2) {
    try {
      // Update global thresholds (expensive but keeps rankings fresh)
      await updateGlobalRankings();
      
      // Check if current user earned new badges
      const newBadges = await checkAndAssignBadges(currentUserId);
      if (newBadges && newBadges.length > 0) {
        newBadges.forEach(badge => {
          useNotificationStore.getState().addNotification({
            type: 'badge',
            title: '¡Nueva Insignia!',
            message: `Has ganado la insignia: ${badge}`,
            icon: 'award'
          });
        });
      }
    } catch (e) {
      console.error('[Rankings] Error updating rankings:', e);
    }
  }
};

const generatePlayerSummary = (game: GameRecord, playerId: string): PlayerGameSummary => {
  const turns = game.turns || [];
  const playerTurns = turns.filter(t => t.playerId === playerId);
  const opponentId = game.player1Id === playerId ? game.player2Id : game.player1Id;
  const opponentTurns = turns.filter(t => t.playerId === opponentId);

  const score = playerId === game.player1Id ? game.player1Score : game.player2Score;
  const opponentScore = playerId === game.player1Id ? game.player2Score : game.player1Score;
  
  const won = game.winnerId === playerId;
  const lost = game.loserId === playerId;
  const draw = game.isDraw;

  const playTurns = playerTurns.filter(t => t.actionType === 'play');
  const turnDurations = playerTurns.map(t => t.durationSec || 0);
  
  const allWords = playerTurns.flatMap(t => t.validWords || []);
  const longestWord = allWords.reduce((a, b) => (a?.length || 0) >= (b?.length || 0) ? a : b, "");

  const premiumBreakdown = { DL: 0, TL: 0, DW: 0, TW: 0 };
  playerTurns.forEach(t => {
    (t.premiumSquaresUsed || []).forEach(p => {
      if (p in premiumBreakdown) premiumBreakdown[p as keyof typeof premiumBreakdown]++;
    });
  });

  const summary: PlayerGameSummary = {
    playerId,
    score,
    won,
    lost,
    draw,
    turnsCount: playerTurns.length,
    avgTurnTime: turnDurations.length ? turnDurations.reduce((a, b) => a + b, 0) / turnDurations.length : 0,
    fastestTurnTime: turnDurations.length ? Math.min(...turnDurations) : 0,
    slowestTurnTime: turnDurations.length ? Math.max(...turnDurations) : 0,
    totalWords: allWords.length,
    averageWordLength: allWords.length ? allWords.reduce((a, b) => a + (b?.length || 0), 0) / allWords.length : 0,
    longestWord,
    totalMoveScore: playTurns.reduce((a, b) => a + (b.score || 0), 0),
    averageMoveScore: playTurns.length ? playTurns.reduce((a, b) => a + (b.score || 0), 0) / playTurns.length : 0,
    bestMoveScore: playTurns.length ? Math.max(...playTurns.map(t => t.score || 0)) : 0,
    moves50plus: playTurns.filter(t => (t.score || 0) >= 50 && (t.score || 0) < 70).length,
    moves70plus: playTurns.filter(t => (t.score || 0) >= 70 && (t.score || 0) < 100).length,
    moves100plus: playTurns.filter(t => (t.score || 0) >= 100).length,
    fullRackUses: playerTurns.filter(t => t.usedAllRack).length,
    swapsCount: playerTurns.filter(t => t.actionType === 'swap').length,
    passCount: playerTurns.filter(t => t.actionType === 'pass').length,
    premiumUsageCount: playerTurns.filter(t => t.usedPremium).length,
    premiumSquareBreakdown: premiumBreakdown,
    boardOpeningMoves: playerTurns.filter(t => t.openedBoard).length,
    boardBlockingMoves: playerTurns.filter(t => t.blockedBoard).length,
    comebackWin: detectComebackWin(game, playerId),
    finalMargin: Math.abs(score - opponentScore),
    resigned: game.endReason === 'resignation' && game.loserId === playerId,
    abandoned: game.endReason === 'abandon' && game.loserId === playerId,
    lostByTimeout: game.endReason === 'timeout' && game.loserId === playerId,
    wonByTimeout: game.endReason === 'timeout' && game.winnerId === playerId,
    styleSnapshot: calculateStyleSnapshot(playerTurns, playTurns, allWords)
  };

  return summary;
};

const calculateStyleSnapshot = (allTurns: GameTurn[], playTurns: GameTurn[], words: string[]) => {
  const turnsCount = allTurns.length || 1;
  
  const premiumUsageRate = allTurns.filter(t => t.usedPremium).length / turnsCount;
  const longWordRate = words.filter(w => (w?.length || 0) >= 6).length / (words.length || 1);
  const shortWordRate = words.filter(w => (w?.length || 0) <= 3).length / (words.length || 1);
  const rackChangeRate = allTurns.filter(t => t.actionType === 'swap').length / turnsCount;
  const passRate = allTurns.filter(t => t.actionType === 'pass').length / turnsCount;
  
  const avgMoveScore = playTurns.length ? playTurns.reduce((a, b) => a + (b.score || 0), 0) / playTurns.length : 0;
  const normalizedAvgScore = Math.min(avgMoveScore / 50, 1);

  const aggressionIndex = (premiumUsageRate * 0.3) + (longWordRate * 0.25) + (normalizedAvgScore * 0.3) + ((1 - passRate) * 0.15);
  
  const boardOpeningRate = allTurns.filter(t => t.openedBoard).length / turnsCount;
  const blockingRate = allTurns.filter(t => t.blockedBoard).length / turnsCount;

  // Simple variance for riskTaking
  const scores = playTurns.map(t => t.score || 0);
  const mean = avgMoveScore;
  const variance = scores.length ? scores.reduce((a, b) => a + Math.pow(b - mean, 2), 0) / scores.length : 0;
  const normalizedVariance = Math.min(Math.sqrt(variance) / 30, 1);

  const riskTakingIndex = (longWordRate * 0.35) + (premiumUsageRate * 0.3) + (normalizedVariance * 0.2) + ((1 - passRate) * 0.15);

  // Endgame efficiency (last 3 turns)
  const lastTurns = allTurns.slice(-3);
  const lastAvg = lastTurns.length ? lastTurns.reduce((a, b) => a + (b.score || 0), 0) / lastTurns.length : 0;
  const endgameEfficiency = Math.min(lastAvg / (avgMoveScore || 1), 1);
  
  const avgTurnTime = allTurns.length ? allTurns.reduce((a, b) => a + (b.durationSec || 0), 0) / allTurns.length : 30;
  const bingoRate = allTurns.filter(t => t.usedAllRack).length / turnsCount;
  
  const totalVowels = words.join('').match(/[aeiouáéíóúü]/gi)?.length || 0;
  const totalConsonants = words.join('').length - totalVowels;
  const vowelConsonantBalance = totalConsonants > 0 ? totalVowels / totalConsonants : 1;

  return {
    aggressionIndex,
    boardOpeningRate,
    blockingRate,
    premiumUsageRate,
    longWordRate,
    shortWordRate,
    rackChangeRate,
    riskTakingIndex,
    comebackRate: 0, // Calculated at aggregation level
    endgameEfficiency,
    averageTurnTime: avgTurnTime,
    bingoRate,
    swapRate: rackChangeRate,
    passRate,
    vowelConsonantBalance,
    averageWordScore: avgMoveScore
  };
};

const detectComebackWin = (game: GameRecord, playerId: string): boolean => {
  if (game.winnerId !== playerId) return false;

  const turns = game.turns || [];
  const halfTurns = Math.floor(turns.length / 2);
  let wasLosingBadly = false;

  // Check scores at each turn after half game
  let p1Score = 0;
  let p2Score = 0;

  for (let i = 0; i < turns.length; i++) {
    const turn = turns[i];
    if (turn.playerId === game.player1Id) p1Score += turn.score || 0;
    else p2Score += turn.score || 0;

    if (i >= halfTurns) {
      if (playerId === game.player1Id && p1Score <= p2Score - 20) wasLosingBadly = true;
      if (playerId === game.player2Id && p2Score <= p1Score - 20) wasLosingBadly = true;
    }
  }

  return wasLosingBadly;
};

const updatePlayerStats = async (playerId: string, summary: PlayerGameSummary, game: GameRecord, isHumanOpponent: boolean = false) => {
  if (playerId === 'ROBOT') return;

  const opponentId = game.player1Id === playerId ? game.player2Id : game.player1Id;
  const pairKey = [playerId, opponentId].sort().join('_');
  const h2hRef = doc(db, 'headToHead', pairKey);
  const h2hSnap = await getDoc(h2hRef);
  const isNewOpponent = !h2hSnap.exists();

  const statsRef = doc(db, 'users', playerId, 'stats', 'global');
  const statsSnap = await getDoc(statsRef);
  const currentStats = statsSnap.exists() ? statsSnap.data() as PlayerStats : getDefaultStats();

  const socialInteractions = (currentStats.socialInteractions || 0) + (isHumanOpponent ? 1 : 0);
  
  const newGamesPlayed = currentStats.gamesPlayed + 1;
  const totalScore = currentStats.totalScore + summary.score;
  const totalTurns = currentStats.totalTurns + summary.turnsCount;
  const totalWords = currentStats.totalWords + summary.totalWords;

  const hiddenTotals = {
    totalMoveScoreAccumulated: (currentStats.hiddenTotals?.totalMoveScoreAccumulated || 0) + summary.totalMoveScore,
    totalWordLengthAccumulated: (currentStats.hiddenTotals?.totalWordLengthAccumulated || 0) + (summary.averageWordLength * summary.totalWords),
    totalTurnTimeAccumulated: (currentStats.hiddenTotals?.totalTurnTimeAccumulated || 0) + (summary.avgTurnTime * summary.turnsCount)
  };

  const newStreak = summary.won ? currentStats.streakCurrent + 1 : (summary.lost ? 0 : currentStats.streakCurrent);

  // Handle unique boards and languages
  const uniqueBoards = currentStats.uniqueBoards || [];
  if (!uniqueBoards.includes(game.boardType)) {
    uniqueBoards.push(game.boardType);
  }

  const uniqueLanguages = currentStats.uniqueLanguages || [];
  if (!uniqueLanguages.includes(game.language)) {
    uniqueLanguages.push(game.language);
  }

  // Handle robot stats
  const robotWins = currentStats.robotWins || {};
  let robotGames = currentStats.robotGames || 0;
  if (opponentId === 'ROBOT') {
    robotGames += 1;
    if (summary.won) {
      const robotLevel = game.robotLevel || 'axel';
      robotWins[robotLevel] = (robotWins[robotLevel] || 0) + 1;
    }
  }

  // Handle days active streak
  const lastPlayDate = currentStats.lastPlayDate?.toDate();
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  let daysActiveStreak = currentStats.daysActiveStreak || 0;
  
  if (lastPlayDate) {
    const lastPlay = new Date(lastPlayDate);
    lastPlay.setHours(0, 0, 0, 0);
    const diffTime = Math.abs(today.getTime() - lastPlay.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) {
      daysActiveStreak += 1;
    } else if (diffDays > 1) {
      daysActiveStreak = 1;
    }
  } else {
    daysActiveStreak = 1;
  }

  const updatedStats: any = {
    gamesPlayed: increment(1),
    gamesFinished: game.status === 'finished' ? increment(1) : currentStats.gamesFinished,
    gamesAbandoned: game.status === 'abandoned' ? increment(1) : currentStats.gamesAbandoned,
    gamesTimeout: game.endReason === 'timeout' ? increment(1) : currentStats.gamesTimeout,
    wins: summary.won ? increment(1) : currentStats.wins,
    losses: summary.lost ? increment(1) : currentStats.losses,
    draws: summary.draw ? increment(1) : currentStats.draws,
    winRate: (currentStats.wins + (summary.won ? 1 : 0)) / newGamesPlayed,
    
    totalScore: increment(summary.score),
    averageScore: totalScore / newGamesPlayed,
    bestScore: Math.max(currentStats.bestScore, summary.score),
    worstScore: currentStats.worstScore === 0 ? summary.score : Math.min(currentStats.worstScore, summary.score),

    totalWords: increment(summary.totalWords),
    averageWordScore: hiddenTotals.totalMoveScoreAccumulated / (totalTurns || 1),
    bestWordScore: Math.max(currentStats.bestWordScore, summary.bestMoveScore),
    bestWord: (summary.longestWord?.length || 0) > (currentStats.bestWord?.length || 0) ? summary.longestWord : currentStats.bestWord,

    totalTurns: increment(summary.turnsCount),
    averageTurnTime: hiddenTotals.totalTurnTimeAccumulated / (totalTurns || 1),
    fastestTurnTime: currentStats.fastestTurnTime === 0 ? summary.fastestTurnTime : Math.min(currentStats.fastestTurnTime, summary.fastestTurnTime),
    slowestTurnTime: Math.max(currentStats.slowestTurnTime, summary.slowestTurnTime),

    moves50plus: increment(summary.moves50plus),
    moves70plus: increment(summary.moves70plus),
    moves100plus: increment(summary.moves100plus),

    fullRackUses: increment(summary.fullRackUses),
    swapsCount: increment(summary.swapsCount),
    passCount: increment(summary.passCount),

    longestWordLength: Math.max(currentStats.longestWordLength || 0, (summary.longestWord?.length || 0)),
    averageWordLength: hiddenTotals.totalWordLengthAccumulated / (totalWords || 1),

    streakCurrent: newStreak,
    streakBest: Math.max(currentStats.streakBest, newStreak),

    uniqueBoards,
    uniqueLanguages,
    robotWins,
    robotGames,
    daysActiveStreak,
    socialInteractions,

    lastPlayDate: Timestamp.now(),
    hiddenTotals
  };

  if (isNewOpponent && opponentId !== 'ROBOT') {
    updatedStats.uniqueOpponents = increment(1);
  }

  await setDoc(statsRef, updatedStats, { merge: true });

  // Update mode stats
  const modeRef = doc(db, 'users', playerId, 'stats', 'mode_' + game.boardType);
  await setDoc(modeRef, updatedStats, { merge: true });

  // Update style metrics
  await updateStyleMetrics(playerId, summary, newGamesPlayed);
};

const updateStyleMetrics = async (playerId: string, summary: PlayerGameSummary, gamesCount: number) => {
  const styleRef = doc(db, 'users', playerId, 'stats', 'style');
  const styleSnap = await getDoc(styleRef);
  const currentStyle = styleSnap.exists() ? styleSnap.data() as PlayerStyleMetrics : getDefaultStyle();

  const hidden = currentStyle.hiddenTotals || {
    totalAggressionIndex: 0,
    totalBoardOpeningRate: 0,
    totalBlockingRate: 0,
    totalPremiumUsageRate: 0,
    totalLongWordRate: 0,
    totalShortWordRate: 0,
    totalRackChangeRate: 0,
    totalRiskTakingIndex: 0,
    totalComebackRate: 0,
    totalEndgameEfficiency: 0,
    totalTurnTime: 0,
    totalBingoRate: 0,
    totalSwapRate: 0,
    totalPassRate: 0,
    totalVowelConsonantBalance: 0,
    totalWordScore: 0,
    styleGamesCount: 0
  };

  hidden.totalAggressionIndex += summary.styleSnapshot.aggressionIndex;
  hidden.totalBoardOpeningRate += summary.styleSnapshot.boardOpeningRate;
  hidden.totalBlockingRate += summary.styleSnapshot.blockingRate;
  hidden.totalPremiumUsageRate += summary.styleSnapshot.premiumUsageRate;
  hidden.totalLongWordRate += summary.styleSnapshot.longWordRate;
  hidden.totalShortWordRate += summary.styleSnapshot.shortWordRate;
  hidden.totalRackChangeRate += summary.styleSnapshot.rackChangeRate;
  hidden.totalRiskTakingIndex += summary.styleSnapshot.riskTakingIndex;
  hidden.totalComebackRate += summary.comebackWin ? 1 : 0;
  hidden.totalEndgameEfficiency += summary.styleSnapshot.endgameEfficiency;
  
  hidden.totalTurnTime += summary.styleSnapshot.averageTurnTime;
  hidden.totalBingoRate += summary.styleSnapshot.bingoRate;
  hidden.totalSwapRate += summary.styleSnapshot.swapRate;
  hidden.totalPassRate += summary.styleSnapshot.passRate;
  hidden.totalVowelConsonantBalance += summary.styleSnapshot.vowelConsonantBalance;
  hidden.totalWordScore += summary.styleSnapshot.averageWordScore;
  
  hidden.styleGamesCount += 1;

  const n = hidden.styleGamesCount;
  const updatedStyle: PlayerStyleMetrics = {
    aggressionIndex: hidden.totalAggressionIndex / n,
    boardOpeningRate: hidden.totalBoardOpeningRate / n,
    blockingRate: hidden.totalBlockingRate / n,
    premiumUsageRate: hidden.totalPremiumUsageRate / n,
    longWordRate: hidden.totalLongWordRate / n,
    shortWordRate: hidden.totalShortWordRate / n,
    rackChangeRate: hidden.totalRackChangeRate / n,
    riskTakingIndex: hidden.totalRiskTakingIndex / n,
    comebackRate: hidden.totalComebackRate / n,
    endgameEfficiency: hidden.totalEndgameEfficiency / n,
    
    averageTurnTime: hidden.totalTurnTime / n,
    bingoRate: hidden.totalBingoRate / n,
    swapRate: hidden.totalSwapRate / n,
    passRate: hidden.totalPassRate / n,
    vowelConsonantBalance: hidden.totalVowelConsonantBalance / n,
    averageWordScore: hidden.totalWordScore / n,
    
    hiddenTotals: hidden
  };

  await setDoc(styleRef, updatedStyle, { merge: true });
};

const updateHeadToHead = async (p1Id: string, p2Id: string, s1: PlayerGameSummary, s2: PlayerGameSummary, game: GameRecord) => {
  const pairKey = [p1Id, p2Id].sort().join('_');
  const h2hRef = doc(db, 'headToHead', pairKey);

  const getH2HData = (myId: string, mySummary: PlayerGameSummary, oppSummary: PlayerGameSummary) => {
    const prefix = `${myId}_stats`;
    const data: any = {
      [`${prefix}.gamesPlayed`]: increment(1),
      [`${prefix}.pointsFor`]: increment(mySummary.score),
      [`${prefix}.pointsAgainst`]: increment(oppSummary.score),
      [`${prefix}.lastPlayedAt`]: Timestamp.now()
    };
    if (mySummary.won) data[`${prefix}.wins`] = increment(1);
    if (mySummary.lost) data[`${prefix}.losses`] = increment(1);
    if (mySummary.draw) data[`${prefix}.draws`] = increment(1);
    return data;
  };

  const data = {
    ...getH2HData(p1Id, s1, s2),
    ...getH2HData(p2Id, s2, s1),
    lastGameId: game.id,
    updatedAt: Timestamp.now()
  };

  try {
    await updateDoc(h2hRef, data);
  } catch (e) {
    // If doc doesn't exist, create it
    await setDoc(h2hRef, data, { merge: true });
  }
};

const updateDeveloperMetrics = async (game: GameRecord, s1: PlayerGameSummary, s2: PlayerGameSummary) => {
  const devRef1 = doc(db, 'developerAnalysis', `${game.id}_${game.player1Id}`);
  const devRef2 = doc(db, 'developerAnalysis', `${game.id}_${game.player2Id}`);

  const createDevAnalysis = (playerId: string, summary: PlayerGameSummary): DeveloperGameAnalysis => {
    const playerTurns = (game.turns || []).filter(t => t.playerId === playerId);
    return {
      gameId: game.id,
      playerId,
      invalidAttemptCount: playerTurns.reduce((a, b) => a + (b.invalidWords?.length || 0), 0),
      passCount: summary.passCount,
      swapCount: summary.swapsCount,
      premiumBreakdown: summary.premiumSquareBreakdown,
      turnDurations: playerTurns.map(t => t.durationSec || 0),
      moveScores: playerTurns.filter(t => t.actionType === 'play').map(t => t.score || 0),
      wordLengths: playerTurns.flatMap(t => t.validWords || []).map(w => w?.length || 0)
    };
  };

  await Promise.all([
    setDoc(devRef1, createDevAnalysis(game.player1Id, s1)),
    setDoc(devRef2, createDevAnalysis(game.player2Id, s2))
  ]);
};

export const incrementSocialInteractions = async (userId: string) => {
  try {
    const statsRef = doc(db, 'users', userId, 'stats', 'global');
    await updateDoc(statsRef, {
      socialInteractions: increment(1)
    });
  } catch (error) {
    console.error("Error incrementing social interactions:", error);
  }
};

const getDefaultStats = (): PlayerStats => ({
  gamesPlayed: 0,
  gamesFinished: 0,
  gamesAbandoned: 0,
  gamesTimeout: 0,
  wins: 0,
  losses: 0,
  draws: 0,
  winRate: 0,
  totalScore: 0,
  averageScore: 0,
  bestScore: 0,
  worstScore: 0,
  totalWords: 0,
  averageWordScore: 0,
  bestWordScore: 0,
  bestWord: '',
  totalTurns: 0,
  averageTurnTime: 0,
  fastestTurnTime: 0,
  slowestTurnTime: 0,
  moves50plus: 0,
  moves70plus: 0,
  moves100plus: 0,
  fullRackUses: 0,
  swapsCount: 0,
  passCount: 0,
  longestWordLength: 0,
  averageWordLength: 0,
  streakCurrent: 0,
  streakBest: 0,
  uniqueOpponents: 0,
  uniqueBoards: [],
  uniqueLanguages: [],
  robotWins: {},
  robotGames: 0,
  daysActiveStreak: 0,
  lastPlayDate: Timestamp.now(),
  daysActive: 0,
  socialInteractions: 0
});

const getDefaultStyle = (): PlayerStyleMetrics => ({
  aggressionIndex: 0.5,
  boardOpeningRate: 0.5,
  blockingRate: 0.5,
  premiumUsageRate: 0.5,
  longWordRate: 0.5,
  shortWordRate: 0.5,
  rackChangeRate: 0.5,
  riskTakingIndex: 0.5,
  comebackRate: 0,
  endgameEfficiency: 0.5,
  averageTurnTime: 30,
  bingoRate: 0,
  swapRate: 0,
  passRate: 0,
  vowelConsonantBalance: 1,
  averageWordScore: 0
});
